import { HSection, Typography } from '@s4l/ds'
import { useS4LContext } from '@s4l/react-context'
import type { CSSProperties } from 'react'
import React, { useMemo } from 'react'
import { useBanner } from '../hooks'
import { BANNER_TYPES, type BannersJSON } from '../types'
import { useTypingAnimation } from './hooks'
import styles from './index.module.css'

interface HeroProps {
  bannersJSON?: BannersJSON
  children?: React.ReactNode
}

function Hero({ bannersJSON, children }: Readonly<HeroProps>) {
  const { i18n } = useS4LContext()
  const { bannerInformation } = useBanner({
    bannersJSON,
    bannerType: BANNER_TYPES.HERO,
  })

  const sentences = useMemo(
    () =>
      bannerInformation?.title.map(sentence => {
        if (sentence.translated) {
          return sentence.text ?? ''
        }

        return i18n.formatMessage({ id: sentence.text ?? '' })
      }) ?? [],
    [i18n, bannerInformation]
  )

  const { displayText, showCursor, currentSentence } = useTypingAnimation({
    sentences,
  })

  const imageSource = bannerInformation?.image?.srcSet

  return (
    <div
      style={
        {
          '--mobile-image': `url('${imageSource?.mobile}')`,
          '--tablet-image': `url('${imageSource?.tablet}')`,
          '--desktop-image': `url('${imageSource?.desktop}')`,
        } as CSSProperties
      }
      className={styles.heroBg}
    >
      <HSection
        justify="center"
        paddingTop={['32px', '48px']}
        paddingBottom={['32px', '24px', '48px']}
        paddingLeftRight={['16px', '40px', '0']}
      >
        <h1 className={styles.typewriter}>
          <span>{i18n.formatMessage({ id: 'hero.best-hotels' })}</span>
          <br />
          <span aria-label={currentSentence}>{displayText}</span>
          {showCursor && <span className={styles.cursor}>|</span>}
        </h1>

        <Typography className={['mt-16', 'mb-32']} as="p" variant="title" size="xs">
          {/* not i18n */}
          Your stay, your rules
        </Typography>
        {children}
      </HSection>
    </div>
  )
}

Hero.displayName = 'Hero'
export default Hero
